import { clampOpacity } from '../../_pure/itemOpacity.js';
export function resolveConnectorProgressiveActivation(state, from, to) {
    const activation = state?.progressiveActivation;
    const front = state?.activationFrontPosition;
    if (!activation || !front)
        return activation;
    if (!front.every(Number.isFinite))
        throw new Error('Connector activation front must contain finite coordinates.');
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const dz = to[2] - from[2];
    const squaredLength = dx * dx + dy * dy + dz * dz;
    const projection = squaredLength === 0 ? 0 : ((front[0] - from[0]) * dx + (front[1] - from[1]) * dy + (front[2] - from[2]) * dz) / squaredLength;
    const fraction = Math.max(0, Math.min(1, projection));
    return { ...activation, progress01: activation.direction === 'from-to' ? fraction : 1 - fraction };
}
export function flattenItemParametricState(itemParametricState) {
    return {
        ...(itemParametricState?.entityItemByKey ?? {}),
        ...(itemParametricState?.connectorItemByKey ?? {}),
    };
}
export function isVisibleByPresentation(args) {
    const { visibility, hoveredEntity, effectiveActiveItemKeyByConstellationKey, effectiveLinkedActiveItemKeysByConstellationKey, } = args;
    if (!visibility)
        return true;
    const visibleByHover = visibility.visibleOnHoverOf !== undefined
        && hoveredEntity !== null
        && hoveredEntity.constellationKey === visibility.visibleOnHoverOf.constellationKey
        && hoveredEntity.itemKey === visibility.visibleOnHoverOf.itemKey;
    const visibleByActive = visibility.visibleOnActiveOf !== undefined
        && (effectiveActiveItemKeyByConstellationKey[visibility.visibleOnActiveOf.constellationKey]
            === visibility.visibleOnActiveOf.itemKey
            || (effectiveLinkedActiveItemKeysByConstellationKey[visibility.visibleOnActiveOf.constellationKey]
                ?.has(visibility.visibleOnActiveOf.itemKey)
                ?? false));
    return visibleByHover || visibleByActive;
}
export function resolveConnectorColor(parametricState, activeItemKeyByConstellationKey = {}, linkedActiveItemKeysByConstellationKey = {}) {
    const highlighted = parametricState?.highlightOnActiveOf?.some(({ constellationKey, itemKey }) => (activeItemKeyByConstellationKey[constellationKey] === itemKey
        || linkedActiveItemKeysByConstellationKey[constellationKey]?.has(itemKey)));
    if (highlighted && parametricState?.visualStyle?.activeHighlightColor) {
        return parametricState.visualStyle.activeHighlightColor;
    }
    const color = parametricState?.visualStyle?.color;
    return typeof color === 'string' && color.length > 0 ? color : 'black';
}
export function resolveConnectorOpacity(parametricState) {
    const opacity = parametricState?.visualStyle?.opacity;
    return typeof opacity === 'number' ? opacity : 1;
}
export function resolveEntityItemColor(parametricState, fallbackColor) {
    const color = parametricState?.visualStyle?.color;
    return typeof color === 'string' && color.length > 0 ? color : fallbackColor;
}
export function resolveEntityItemActiveHighlightColor(parametricState) {
    const color = parametricState?.visualStyle?.activeHighlightColor;
    return typeof color === 'string' && color.length > 0 ? color : undefined;
}
export function resolveEntityItemLinkedHighlightColor(parametricState) {
    const color = parametricState?.visualStyle?.linkedHighlightColor;
    return typeof color === 'string' && color.length > 0 ? color : undefined;
}
export function resolveConnectorPathKind(parametricState) {
    const pathKind = parametricState?.path?.pathKind;
    return pathKind === 'curved' || pathKind === 'bracket' ? pathKind : 'straight';
}
export function resolveConnectorCurvePlane(parametricState) {
    const plane = parametricState?.path?.curvePlane;
    if (plane === 'xz' || plane === 'xy' || plane === 'yz' || plane === 'auto')
        return plane;
    return 'auto';
}
export function resolveConnectorCurveStrength(parametricState) {
    const strength = parametricState?.path?.curveStrength;
    return typeof strength === 'number' && Number.isFinite(strength) ? Math.max(strength, 0) : 0.15;
}
export function resolveConnectorCurveMinOffset(parametricState) {
    const minOffset = parametricState?.path?.curveMinOffset;
    return typeof minOffset === 'number' && Number.isFinite(minOffset) ? Math.max(minOffset, 0) : 0.25;
}
export function resolveConnectorBracketPoints(args) {
    const { start, end, parametricState } = args;
    const offset = parametricState?.path?.bracketOffset ?? 0.5;
    if (!Number.isFinite(offset) || offset <= 0) {
        throw new Error('Connector bracketOffset must be finite and greater than zero.');
    }
    const control = resolveConnectorCurveControlPoint({
        start, end,
        curvePlane: resolveConnectorCurvePlane(parametricState),
        curveDirection: resolveConnectorCurveDirection(parametricState),
        curveStrength: 0,
        curveMinOffset: offset,
    });
    // Coincident endpoints in the selected plane have no perpendicular track.
    if (control === null)
        return [start, end];
    const displacement = [
        control[0] - (start[0] + end[0]) / 2,
        control[1] - (start[1] + end[1]) / 2,
        control[2] - (start[2] + end[2]) / 2,
    ];
    const shifted = (point) => [
        point[0] + displacement[0], point[1] + displacement[1], point[2] + displacement[2],
    ];
    return [start, shifted(start), shifted(end), end];
}
export function resolveConnectorCurveDirection(parametricState) {
    const direction = parametricState?.path?.curveDirection;
    if (direction === 'positive' || direction === 'negative' || direction === 'auto')
        return direction;
    return 'auto';
}
export function resolveAutoPlane(start, end) {
    const dx = Math.abs(end[0] - start[0]);
    const dy = Math.abs(end[1] - start[1]);
    const dz = Math.abs(end[2] - start[2]);
    if (dy <= dx && dy <= dz)
        return 'xz';
    if (dz <= dx && dz <= dy)
        return 'xy';
    return 'yz';
}
export function resolveAutoDirectionSign(args) {
    const { start, end, plane } = args;
    let a0 = 0;
    let a1 = 0;
    let b0 = 0;
    let b1 = 0;
    if (plane === 'xz') {
        a0 = start[0];
        a1 = start[2];
        b0 = end[0];
        b1 = end[2];
    }
    else if (plane === 'xy') {
        a0 = start[0];
        a1 = start[1];
        b0 = end[0];
        b1 = end[1];
    }
    else {
        a0 = start[1];
        a1 = start[2];
        b0 = end[1];
        b1 = end[2];
    }
    if (a0 === b0) {
        return a1 <= b1 ? 1 : -1;
    }
    return a0 <= b0 ? 1 : -1;
}
export function resolveConnectorCurveControlPoint(args) {
    const { start, end, curvePlane, curveStrength = 0.15, curveMinOffset = 0.25, curveDirection = 'auto', } = args;
    const plane = curvePlane === 'auto' ? resolveAutoPlane(start, end) : curvePlane;
    const sign = curveDirection === 'positive'
        ? 1
        : curveDirection === 'negative'
            ? -1
            : resolveAutoDirectionSign({ start, end, plane });
    const mx = (start[0] + end[0]) / 2;
    const my = (start[1] + end[1]) / 2;
    const mz = (start[2] + end[2]) / 2;
    if (plane === 'xz') {
        const dx = end[0] - start[0];
        const dz = end[2] - start[2];
        const projectedLength = Math.hypot(dx, dz);
        if (projectedLength <= 1e-6)
            return null;
        const offset = Math.max(curveMinOffset, projectedLength * curveStrength);
        const nx = (-dz / projectedLength) * sign;
        const nz = (dx / projectedLength) * sign;
        return [mx + nx * offset, my, mz + nz * offset];
    }
    if (plane === 'xy') {
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const projectedLength = Math.hypot(dx, dy);
        if (projectedLength <= 1e-6)
            return null;
        const offset = Math.max(curveMinOffset, projectedLength * curveStrength);
        const nx = (-dy / projectedLength) * sign;
        const ny = (dx / projectedLength) * sign;
        return [mx + nx * offset, my + ny * offset, mz];
    }
    const dy = end[1] - start[1];
    const dz = end[2] - start[2];
    const projectedLength = Math.hypot(dy, dz);
    if (projectedLength <= 1e-6)
        return null;
    const offset = Math.max(curveMinOffset, projectedLength * curveStrength);
    const ny = (-dz / projectedLength) * sign;
    const nz = (dy / projectedLength) * sign;
    return [mx, my + ny * offset, mz + nz * offset];
}
